// Failed jobs (CHANGE-070): why each one failed, and Retry. A token reads this list, so it shows no payload. Retry uses the
// old route, which keeps its own checks. A retried job may move money or send mail, so it asks first.
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../../../lib'
import { Busy, Button, Err, Fresh, Page, Sources, Table, ago, since, errorText, num, useCursor, useKeys, useKit, when, type Column } from '../kit'

type Job = { id: string; kind: string; attempts: number; last_error: string | null; created_at: string }
type Result = { rows: Job[]; next_cursor: string | null; total: number; at: string }
const KEY = ['staff', 'system', 'jobs']

export function JobsPage() {
  const qc = useQueryClient()
  const kit = useKit()
  const navigate = useNavigate()
  const q = useInfiniteQuery({
    queryKey: KEY,
    initialPageParam: '',
    queryFn: ({ pageParam }) => api<Result>('GET', `/api/staff/system/jobs?cursor=${encodeURIComponent(pageParam)}`),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    refetchInterval: (s) => ((s.state.data?.pages.length ?? 1) > 1 ? false : 30_000),
  })
  const retry = useMutation({
    mutationFn: (id: string) => api('POST', `/api/staff/jobs/${id}/retry`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: ['staff', 'counts'] })
      kit.toast('Back in the queue')
    },
    onError: (e) => { qc.invalidateQueries({ queryKey: KEY }); kit.toast(errorText(e), { bad: true }) },
  })
  const rows = q.data?.pages.flatMap((p) => p.rows) ?? []
  const last = q.data?.pages[q.data.pages.length - 1]
  const askRetry = async (j: Job) => {
    if (retry.isPending) return
    const r = await kit.confirm({ title: `Retry ${j.kind}`, body: `It runs again from the start, with ${j.attempts} failed attempts wiped.`, confirm: 'Retry' })
    if (r.ok) retry.mutate(j.id)
  }
  const { cur, row } = useCursor(rows, (j) => j.id, (j) => navigate(`/staff/go/${j.id}`))
  useKeys({ r: () => { if (row) askRetry(row) } })

  const columns: Column<Job>[] = [
    { key: 'kind', head: 'Job', primary: true, cell: (j) => <Link className="st-link" to={`/staff/go/${j.id}`} data-testid="staff-job-row">{j.kind}</Link> },
    { key: 'attempts', head: 'Attempts', num: true, cell: (j) => j.attempts },
    { key: 'error', head: 'Error', cell: (j) => <span className="st-sys-error">{j.last_error ?? 'None recorded'}</span> },
    { key: 'created', head: 'Created', nowrap: true, cell: (j) => <span title={when(j.created_at)}>{since(j.created_at)}</span> },
    { key: 'do', head: '', label: 'Do', cell: (j) => (
      <Button disabled={retry.isPending && retry.variables === j.id} onClick={() => askRetry(j)} data-testid="staff-job-retry">Retry</Button>
    ) },
  ]
  return (
    <Page title="Jobs" sub={last ? `${num(last.total)} failed` : undefined} testid="staff-jobs">
      {last ? <Sources><Fresh at={last.at} live label="Jobs table" limit={120} testid="jobs-fresh" /></Sources> : null}
      {q.isLoading ? <Busy /> : !q.data ? <Err error={q.error} /> : (
        <>
          {q.error ? <Err error={q.error} /> : null}
          <Table testid="staff-job-table" rows={rows} rowKey={(j) => j.id} columns={columns} cur={cur} empty="No failed jobs" />
          {q.hasNextPage ? <div><Button tone="quiet" disabled={q.isFetchingNextPage} onClick={() => q.fetchNextPage()}>More</Button></div> : null}
        </>
      )}
    </Page>
  )
}
